import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * Round 52 · Project C v1 — Dynamic Theme & Celebration Engine.
 *
 * Fetches /api/theme/current on mount (and every 30 min) so the app's accent
 * colour, splash and greetings reflect Ghana public holidays, festivals and
 * the user's birthday — without needing a redeploy.
 *
 * Theme payload shape (see backend/routes/round52_dashboard_ia.py):
 *   { id, name, palette, splash_emoji, greeting, is_birthday, is_holiday }
 */
const DEFAULT_THEME = { id: "default", name: "iCaard", palette: "#147D60", splash_emoji: "", greeting: "", is_birthday: false, is_holiday: false };

const ThemeCtx = createContext(DEFAULT_THEME);

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(DEFAULT_THEME);

  useEffect(() => {
    let stop = false;
    const fetchTheme = async () => {
      // Anonymous visitors (landing/auth pages) keep the default theme —
      // /theme/current requires auth and would just log a 401.
      if (!localStorage.getItem("icard_token")) return;
      try {
        const r = await api.get("/theme/current");
        if (!stop && r.data) setTheme({ ...DEFAULT_THEME, ...r.data, palette: (!r.data.is_holiday && !r.data.is_birthday) ? DEFAULT_THEME.palette : r.data.palette || DEFAULT_THEME.palette });
      } catch (_) { /* no token / network — keep default */ }
    };
    fetchTheme();
    const t = setInterval(fetchTheme, 30 * 60 * 1000);   // re-check twice an hour
    return () => { stop = true; clearInterval(t); };
  }, []);

  // Surface the accent colour as a CSS variable so any component can read it.
  useEffect(() => {
    document.documentElement.style.setProperty("--theme-accent", theme.palette || DEFAULT_THEME.palette);
  }, [theme.palette]);

  return <ThemeCtx.Provider value={theme}>{children}</ThemeCtx.Provider>;
}

export const useTheme = () => useContext(ThemeCtx);
